import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronLeft,
  faChevronRight,
  faRefresh,
  faSearch,
  faSortUp,
  faSortDown,
  faSort,
  faL,
} from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { API_URL } from "../config.js";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import moment from "moment";
import { showToast } from "../components/toast.jsx";
import AddFreeServicePopup from "./AddFreeServicePopup.jsx";
import AddPaidServiceOrderPopup from "./AddPaidServiceOrderPopup.jsx";
import BulkEditPopup from "./BulkEditPopup.jsx";
import AddCreditsPopup from "./AddCreditsPopup.jsx";
import SendSmsBulkPopup from "./SendSmsBulkPopup.jsx";
import SendPushNotificationPopup from "./SendPushNotificationPopup.jsx";
import { PermissionWrapper, hasPermission } from "../helpers/permission.js";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { addMonths, subMonths, startOfMonth } from "date-fns";
import usePersistedState from "../Subscriber/usePersistedState.jsx";

const FieldPermissionWrapper = ({ field, children }) => {
  const hasFieldViewPermission = hasPermission(field, "view");
  return hasFieldViewPermission ? children : null;
};

// Helper function to parse single range string into min and max values
const parseNumericRange = (value) => {
  if (!value || typeof value !== "string") return { min: undefined, max: undefined };
  const trimmed = value.trim();
  if (!trimmed) return { min: undefined, max: undefined };

  // Match range like "100-200", "100 - 200", "100 to 200"
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)$/i);
  if (rangeMatch) {
    return { min: rangeMatch[1], max: rangeMatch[2] };
  }

  // Match ">=100", ">100", "100+"
  const gteMatch = trimmed.match(/^(?:>=?|>)\s*(\d+(?:\.\d+)?)$/) || trimmed.match(/^(\d+(?:\.\d+)?)\+$/);
  if (gteMatch) {
    return { min: gteMatch[1], max: undefined };
  }

  // Match "<=200", "<200"
  const lteMatch = trimmed.match(/^(?:<=?|<)\s*(\d+(?:\.\d+)?)$/);
  if (lteMatch) {
    return { min: undefined, max: lteMatch[1] };
  }

  // Single number "100": set both min and max for exact match
  const singleMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
  if (singleMatch) {
    return { min: singleMatch[1], max: singleMatch[1] };
  }

  return { min: undefined, max: undefined };
};

const Subscription = () => {
  const token = localStorage.getItem("authToken");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 100;
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = usePersistedState("subscription_sortField", ""); // example: 'created_on'
  const [sortOrder, setSortOrder] = usePersistedState("subscription_sortOrder", "asc"); // 'asc' or 'desc'
  const navigate = useNavigate();
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  const [checkingAll, setCheckingAll] = useState(false);

  const toggleCheckingAll = () => {
    setCheckingAll(!checkingAll);
  };

  const sortBox = (field) => {
    if (sortField === field) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      fetchSubscriptions(1); // refetch data on toggle
    } else {
      setSortField(field);
      setSortOrder("asc");
      fetchSubscriptions(1); // refetch data on new field
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? faSortUp : faSortDown;
    }
    return faSort; // default icon for inactive columns
  };

  // filters
  const [joinDateFilter, setJoinDateFilter] = usePersistedState(
    "subscription_joinDateFilter",
    [null, null]
  );
  const [nameFilter, setNameFilter] = usePersistedState(
    "subscription_nameFilter",
    ""
  );
  const [mobileFilter, setMobileFilter] = usePersistedState(
    "subscription_mobileFilter",
    ""
  );
  const [leadSourceFilter, setLeadSourceFilter] = usePersistedState(
    "subscription_leadSourceFilter",
    ""
  );
  const [subscriberCreatedDateFilter, setSubscriberCreatedDateFilter] =
    usePersistedState("subscription_subscriberCreatedDateFilter", [null, null]);
  const [serviceFilter, setServiceFilter] = usePersistedState(
    "subscription_serviceFilter",
    ""
  );
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = usePersistedState(
    "subscription_subscriptionTypeFilter",
    ""
  );
  const [serviceStatusFilter, setServiceStatusFilter] = usePersistedState(
    "subscription_serviceStatusFilter",
    ""
  );
  const [serviceTypeFilter, setServiceTypeFilter] = usePersistedState(
    "subscription_serviceTypeFilter",
    ""
  );
  const [planTypeFilter, setPlanTypeFilter] = usePersistedState(
    "subscription_planTypeFilter",
    ""
  );
  const [amountFilter, setAmountFilter] = usePersistedState(
    "subscription_amountFilter",
    ""
  );
  const [actualAmountFilter, setActualAmountFilter] = usePersistedState(
    "subscription_actualAmountFilter",
    ""
  );
  const [activtionDateFilter, setActivtionDateFilter] = usePersistedState(
    "subscription_activtionDateFilter",
    [null, null]
  );
  const [expiryDateFilter, setExpiryDateFilter] = usePersistedState(
    "subscription_expiryDateFilter",
    [null, null]
  );
  const [onHoldDateFilter, setOnHoldDateFilter] = usePersistedState(
    "subscription_onHoldDateFilter",
    [null, null]
  );
  const [reactivationDateFilter, setReactivationDateFilter] =
    usePersistedState("subscription_reactivationDateFilter", [null, null]);
  const [totalCreditsFilter, setTotalCreditsFilter] = usePersistedState(
    "subscription_totalCreditsFilter",
    ""
  );
  const [usedCreditsFilter, setUsedCreditsFilter] = usePersistedState(
    "subscription_usedCreditsFilter",
    ""
  );
  const [availableCreditsFilter, setAvailableCreditsFilter] = usePersistedState(
    "subscription_availableCreditsFilter",
    ""
  );
  const [salesManagerFilter, setSalesManagerFilter] = usePersistedState(
    "subscription_salesManagerFilter",
    ""
  );
  const [assignedUserFilter, setAssignedUserFilter] = usePersistedState(
    "subscription_assignedUserFilter",
    ""
  );
  const [updatedByFilter, setUpdatedByFilter] = usePersistedState(
    "subscription_updatedByFilter",
    ""
  );
  const [utmSourceFilter, setUtmSourceFilter] = usePersistedState(
    "subscription_utmSourceFilter",
    ""
  );
  const [utmMediumFilter, setUtmMediumFilter] = usePersistedState(
    "subscription_utmMediumFilter",
    ""
  );
  const [utmCampaignFilter, setUtmCampaignFilter] = usePersistedState(
    "subscription_utmCampaignFilter",
    ""
  );
  const [utmContentFilter, setUtmContentFilter] = usePersistedState(
    "subscription_utmContentFilter",
    ""
  );
  const [utmTermFilter, setUtmTermFilter] = usePersistedState(
    "subscription_utmTermFilter",
    ""
  );
  const [isLoading, setIsLoading] = useState(false);
  // Add selectedSubscriptions state
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);

  const [dropdownOptions, setDropdownOptions] = useState({
    leadSourceList: [],
    serviceList: [],
    subscriptionTypeList: [],
    serviceStatusList: [],
    serviceTypeList: [],
    userList: [],
    fullUserList: [],
    salesManagerList: [],
    utmSourceList: [],
    utmMediumList: [],
    utmCampaignList: [],
    utmContentList: [],
    utmTermList: [],
    languageList: [],
    stateList: [],
    paidTradesList: [],
    approvalList: [],
    templatePushNotificationList: [],
    templateSmsList: [],
  });

  const buildFilterParams = () => {
    const filterParams = {};
    if (joinDateFilter[0]) {
      filterParams.screated_on = moment(joinDateFilter[0]).format("YYYY-MM-DD");
      filterParams.ecreated_on = moment(
        joinDateFilter[1] || joinDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (nameFilter) filterParams.fullname = nameFilter;
    if (mobileFilter) filterParams.mobileno = mobileFilter;
    if (leadSourceFilter) filterParams.lead_source = leadSourceFilter;
    if (subscriberCreatedDateFilter[0]) {
      filterParams.ssubscriber_created_date = moment(
        subscriberCreatedDateFilter[0]
      ).format("YYYY-MM-DD");
      filterParams.esubscriber_created_date = moment(
        subscriberCreatedDateFilter[1] || subscriberCreatedDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (serviceFilter) filterParams.serviceid = serviceFilter;
    if (subscriptionTypeFilter) filterParams.stype = subscriptionTypeFilter;
    if (serviceStatusFilter) filterParams.status = serviceStatusFilter;
    if (serviceTypeFilter) filterParams.servicetype = serviceTypeFilter;
    if (planTypeFilter) filterParams.plantype = planTypeFilter;
    if (amountFilter) {
      const range = parseNumericRange(amountFilter);
      if (range.min !== undefined) filterParams.min_amount = range.min;
      if (range.max !== undefined) filterParams.max_amount = range.max;
    }
    if (actualAmountFilter) filterParams.actual_amount = actualAmountFilter;
    if (activtionDateFilter[0]) {
      filterParams.sactivation_date = moment(activtionDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.eactivation_date = moment(
        activtionDateFilter[1] || activtionDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (expiryDateFilter[0]) {
      filterParams.sexpiry_date = moment(expiryDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.eexpiry_date = moment(
        expiryDateFilter[1] || expiryDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (onHoldDateFilter[0]) {
      filterParams.sonhold_date = moment(onHoldDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.eonhold_date = moment(
        onHoldDateFilter[1] || onHoldDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (reactivationDateFilter[0]) {
      filterParams.sreactivation_date = moment(
        reactivationDateFilter[0]
      ).format("YYYY-MM-DD");
      filterParams.ereactivation_date = moment(
        reactivationDateFilter[1] || reactivationDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (totalCreditsFilter) {
      const range = parseNumericRange(totalCreditsFilter);
      if (range.min !== undefined) filterParams.min_trades_total = range.min;
      if (range.max !== undefined) filterParams.max_trades_total = range.max;
    }
    if (usedCreditsFilter) {
      const range = parseNumericRange(usedCreditsFilter);
      if (range.min !== undefined) filterParams.min_trades_used = range.min;
      if (range.max !== undefined) filterParams.max_trades_used = range.max;
    }
    if (availableCreditsFilter) {
      const range = parseNumericRange(availableCreditsFilter);
      if (range.min !== undefined) filterParams.min_trades_available = range.min;
      if (range.max !== undefined) filterParams.max_trades_available = range.max;
    }
    if (salesManagerFilter) filterParams.sales_manager = salesManagerFilter;
    if (assignedUserFilter) filterParams.assigned_user = assignedUserFilter;
    if (updatedByFilter) filterParams.updated_by = updatedByFilter;
    if (utmSourceFilter) filterParams.utm_source = utmSourceFilter;
    if (utmMediumFilter) filterParams.utm_medium = utmMediumFilter;
    if (utmCampaignFilter) filterParams.utm_campaign = utmCampaignFilter;
    if (utmContentFilter) filterParams.utm_content = utmContentFilter;
    if (utmTermFilter) filterParams.utm_term = utmTermFilter;
    if (sortField) filterParams.orderbycol = sortField;
    if (sortOrder) filterParams.orderby = sortOrder;

    return filterParams;
  };

  const handleRowClick = (id) => {
    if (!hasPermission("subscription", "view")) {
      return;
    }
    const filterParams = buildFilterParams();

    navigate(`/Subscription/Edit/${id}`, {
      state: {
        filterParams,
      },
    });
  };

  const fetchSubscriptions = async (pageNumber = 1) => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      const filterParams = buildFilterParams();

      const response = await axios.post(
        `${API_URL}subscription?limit=${limit}&page=${pageNumber}`,
        filterParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("response", response);

      if (response.data.success) {
        const { subscriptions, totalCount, totalPages } = response.data.data;
        setUsers(subscriptions || []);
        setFilteredUsers(subscriptions || []);
        setTotalPages(totalPages);
        setTotalRecords(totalCount);
      } else {
        setError(response.data.message || "Failed to fetch subscriptions");
        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setError(
        error.message || "An error occurred while fetching subscriptions"
      );
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}subscription/get-dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.data) {
        setDropdownOptions({
          leadSourceList: response.data.data.lead_sourcelist || [],
          serviceList: response.data.data.servicelist || [],
          subscriptionTypeList: response.data.data.subscription_typelist || [],
          serviceStatusList: response.data.data.service_statuslist || [],
          serviceTypeList: response.data.data.service_typelist || [],
          userList: response.data.data.assignUser || [],
          fullUserList: response.data.data.fulluserlist || [],
          salesManagerList: response.data.data.salesManagerlist || [],
          utmSourceList: response.data.data.utmsourcelist || [],
          utmMediumList: response.data.data.utmmediumlist || [],
          utmCampaignList: response.data.data.utmcampaignlist || [],
          utmContentList: response.data.data.utmcontentlist || [],
          utmTermList: response.data.data.utmtermlist || [],
          languageList: response.data.data.languagelist || [],
          stateList: response.data.data.statelist || [],
          paidTradesList: response.data.data.paidtradelist || [],
          approvalList: response.data.data.approvallist || [],
          templatePushNotificationList:
            response.data.data.templatePushNotificationlist || [],
          templateSmsList: response.data.data.templateSmslist || [],
          distributorList: response.data.data.distributorList || [],
        });
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions(page);
  }, [
    page,
    leadSourceFilter,
    serviceFilter,
    subscriptionTypeFilter,
    serviceStatusFilter,
    serviceTypeFilter,
    planTypeFilter,
    salesManagerFilter,
    assignedUserFilter,
    updatedByFilter,
    utmSourceFilter,
    utmMediumFilter,
    utmCampaignFilter,
    utmContentFilter,
    utmTermFilter,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    fetchDropdownOptions();
    // handleTemplateChange();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalRecords);

  const [isModalOpenPaidServiceOrder, setIsModalOpenPaidServiceOrder] =
    useState(false);
  const [isModalOpenFreeService, setIsModalOpenFreeService] = useState(false);
  const [isModalOpenBulkEdit, setIsModalOpenBulkEdit] = useState(false);
  const [isModalOpenCreditsAdd, setIsModalOpenCreditsAdd] = useState(false);
  const [isModalOpenSendSms, setIsModalOpenSendSms] = useState(false);
  const [isModalOpenSendPush, setIsModalOpenSendPush] = useState(false);
  const [isModalOpenSendEmail, setIsModalOpenSendEmail] = useState(false);
  const [emailData, setEmailData] = useState({
    emailtemp: "",
    subject: "",
    message: "",
    file: null,
  });
  const [templateList, setTemplateList] = useState([]);

  const handleTemplateChange = async (templateId) => {
    if (!templateId) {
      setEmailData((prev) => ({
        ...prev,
        subject: "",
        message: "",
      }));
      return;
    }
    // Always update selected template ID immediately
    setEmailData((prev) => ({
      ...prev,
      emailtemp: templateId,
    }));

    if (!templateId) {
      setEmailData((prev) => ({
        ...prev,
        subject: "",
        message: "",
      }));
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}settings/template/email/${templateId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const template = response.data.data;
        setEmailData((prev) => ({
          ...prev,
          subject: template.subject || "",
          message: template.message || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching template:", error);
      showToast.error("Failed to load template");
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!emailData.subject || !emailData.message) {
      showToast.warning("Please fill in all required fields");
      return;
    }

    const filterParams = {};
    // Add all your existing filter params here as in other handlers

    try {
      const formData = new FormData();
      formData.append("subject", emailData.subject);
      formData.append("message", emailData.message);
      formData.append("emailtemp", emailData.emailtemp);
      if (emailData.file) {
        formData.append("file", emailData.file);
      }
      formData.append("checkingAll", checkingAll ? 1 : 0);
      formData.append("selectedIds", JSON.stringify(selectedSubscriptions));
      formData.append("filters", JSON.stringify(filterParams));

      const response = await axios.post(
        `${API_URL}subscription/send-email-bulk`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        showToast.success(response.data.message || "Emails sent successfully");
        setIsModalOpenSendEmail(false);
        setSelectedSubscriptions([]);
        setEmailData({
          emailtemp: "",
          subject: "",
          message: "",
          file: null,
        });
      } else {
        showToast.error(response.data.message || "Failed to send emails");
      }
    } catch (error) {
      console.error("Error sending emails:", error);
      showToast.error("An error occurred while sending emails");
    }
  };

  useEffect(() => {
    const fetchEmailTemplates = async () => {
      try {
        const response = await axios.get(
          `${API_URL}settings/template/email/list`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("fetchEmailTemplates", response);

        // Access the data array directly from response.data.data
        const templates = response.data.data;
        if (Array.isArray(templates)) {
          setTemplateList(templates);
        } else {
          setTemplateList([]);
        }
      } catch (error) {
        console.error("Error fetching email templates:", error);
      }
    };

    fetchEmailTemplates();
  }, []);

  // Close the modal
  const closeModal = () => {
    setIsModalOpenPaidServiceOrder(false);
    setIsModalOpenFreeService(false);
    setIsModalOpenBulkEdit(false);
    setIsModalOpenCreditsAdd(false);
    setIsModalOpenSendSms(false);
    setIsModalOpenSendPush(false);
  };

  // Toggle the modal open/close
  const toggleModalPaidServiceOrder = () => {
    setIsModalOpenPaidServiceOrder(!isModalOpenPaidServiceOrder);
  };
  const toggleModalFreeService = () => {
    setIsModalOpenFreeService(!isModalOpenFreeService);
  };

  // Handle date picker change
  const handleJoinDateChange = (dates) => {
    setJoinDateFilter(dates);
  };
  const handleSubscriberDateChange = (dates) => {
    setSubscriberCreatedDateFilter(dates);
  };
  const handleActivationDateChange = (dates) => {
    setActivtionDateFilter(dates);
  };
  const handleExpiryDateChange = (dates) => {
    setExpiryDateFilter(dates);
  };
  const handleOnHoldDateChange = (dates) => {
    setOnHoldDateFilter(dates);
  };
  const handleReactivationDateChange = (dates) => {
    setReactivationDateFilter(dates);
  };

  const handleRefresh = () => {
    fetchSubscriptions(1);
  };

  const handleCheckboxChange = (id, checked) => {
    if (checked) {
      setSelectedSubscriptions((prev) => [...prev, id]);
    } else {
      setSelectedSubscriptions((prev) => prev.filter((subId) => subId !== id));
    }
  };

  const handleActionChange = async (action) => {
    if (action === "delete") {
      if (selectedSubscriptions.length === 0) {
        showToast.warning("Please select at least one subscription to delete");
        return;
      }

      if (
        window.confirm(
          `Are you sure you want to delete ${selectedSubscriptions.length} subscription(s)?`
        )
      ) {
        try {
          const filterParams = buildFilterParams();

          const payload = {
            checkingAll: checkingAll ? 1 : 0,
            filter: filterParams,
            del: selectedSubscriptions,
          };

          const response = await axios.post(
            `${API_URL}subscription/delete`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data.success === true) {
            showToast.success(
              response.data.message || "Subscriptions deleted successfully"
            );
            setSelectedSubscriptions([]);
            fetchSubscriptions();
          }
        } catch (error) {
          console.error("Error deleting subscriptions:", error);
          console.log(error.response?.data || "Unknown error");
          showToast.error("An error occurred while deleting subscriptions");
        }
      }
    } else if (action === "bulkExport") {
      if (selectedSubscriptions.length === 0) {
        showToast.warning("Please select at least one subscription to export");
        return;
      }

      handleBulkExport(selectedSubscriptions);
    } else if (action === "bulkEdit") {
      if (selectedSubscriptions.length === 0) {
        showToast.warning("Please select at least one subscription to edit");
        return;
      }

      setIsModalOpenBulkEdit(true);
    } else if (action === "addCredits") {
      setIsModalOpenCreditsAdd(true);
    } else if (action === "sendSms") {
      if (selectedSubscriptions.length === 0) {
        showToast.warning(
          "Please select at least one subscription to send sms"
        );
        return;
      }

      setIsModalOpenSendSms(true);
    } else if (action === "sendPush") {
      if (selectedSubscriptions.length === 0) {
        showToast.warning(
          "Please select at least one subscription to send sms"
        );
        return;
      }

      setIsModalOpenSendPush(true);
    } else if (action === "sendEmail") {
      if (selectedSubscriptions.length === 0) {
        showToast.warning(
          "Please select at least one subscription to send email"
        );
        return;
      }
      setIsModalOpenSendEmail(true);
    }
  };

  const handleBulkEditSubmit = async (bulkEditData) => {
    if (selectedSubscriptions.length === 0) {
      showToast.warning("No subscription selected for update");
      return;
    }

    // Filter out empty fields to only update fields that have been changed
    const updateData = {};
    Object.keys(bulkEditData).forEach((key) => {
      if (bulkEditData[key] !== "") {
        // Convert ID to actual value based on the field type
        let value = bulkEditData[key];

        switch (key) {
          case "serviceid":
            const serviceItem = dropdownOptions.serviceList.find(
              (item) => item.id == value
            );
            value = serviceItem ? serviceItem.id : value;
            break;

          case "status":
            const statusItem = dropdownOptions.serviceStatusList.find(
              (item) => item.pick_name == value
            );
            value = statusItem ? statusItem.pick_name : value;
            break;

          case "stype":
            const stypeItem = dropdownOptions.subscriptionTypeList.find(
              (item) => item.pick_name == value
            );
            value = stypeItem ? stypeItem.pick_name : value;
            break;

          case "servicetype":
            const serviceTypeItem = dropdownOptions.serviceTypeList.find(
              (item) => item.pick_name == value
            );
            value = serviceTypeItem ? serviceTypeItem.pick_name : value;
            break;

          case "order_approval":
            const approvalItem = dropdownOptions.approvalList.find(
              (item) => item.pick_name == value
            );
            value = approvalItem ? approvalItem.pick_name : value;
            break;

          case "amount":
            const amount = value;
            value = amount ? Number(value) : Number(value);
            break;

          case "pay_date":
            const payDate = value;
            value = payDate ? value : value;
            break;

          case "pay_mode":
            const payMode = value;
            value = payMode ? value : value;
            break;

          case "pay_refno":
            const payRef = value;
            value = payRef ? value : value;
            break;

          case "sales_manager":
            const salesManager = dropdownOptions.salesManagerList.find(
              (item) => item.id == value
            );
            value = salesManager ? salesManager.id : value;
            break;

          case "activation_date":
            const activationDate = value;
            value = activationDate ? value : value;
            break;

          case "expiry_date":
            const expiryDate = value;
            value = expiryDate ? value : value;
            break;
        }

        updateData[key] = value;
      }
    });

    if (Object.keys(updateData).length === 0) {
      alert("No changes to update");
      return;
    }

    try {
      setIsLoading(true);

      const filterParams = buildFilterParams();

      const payload = {
        checkingAll: checkingAll ? 1 : 0,
        subscriptionIds: selectedSubscriptions,
        filter: filterParams,
        ...updateData,
      };

      console.log("Sending payload:", payload);

      const response = await axios.post(
        `${API_URL}subscription/bulk-update`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast.success(
          response.data.message || "Subscribers updated successfully"
        );
        fetchSubscriptions();

        // Close the modal after a delay
        setTimeout(() => {
          // Clear selected subscribers after successful update
          setSelectedSubscriptions([]);
        }, 2000);
      } else {
        showToast.error(
          response.data.message || "Failed to update subscribers"
        );
      }
    } catch (error) {
      console.error("Error updating subscribers:", error);

      if (error.response) {
        console.log("Error response data:", error.response.data);
        console.log("Error response status:", error.response.status);
        console.log("Error response headers:", error.response.headers);

        showToast.error(error.response.data.message || "Server error occurred");
      } else if (error.request) {
        console.log("Error request:", error.request);
        showToast.error("No response received from server");
      } else {
        console.log("Error message:", error.message);
        showToast.error("Error setting up request: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkExport = async (selectedSubscriptions) => {
    const filterParams = buildFilterParams();

    try {
      const response = await axios.post(
        `${API_URL}subscription/export`,
        { selectedIds: selectedSubscriptions, filters: filterParams },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          params: {
            checkAll: checkingAll,
          },
          responseType: "blob",
        }
      );

      if (response.status == 201) {
        const now = new Date();
        const formattedDate = now
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
          .replace(/[/, ]+/g, "_"); // Replace separators with underscores

        const fileName = `SubscriptionList-${formattedDate}.xlsx`;

        // ✅ Create a downloadable link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.log(error.response?.data || "Unknown error");
      showToast.error("An error occurred while export subscribers");
    }
  };

  const handleSendSmsSubmit = async (sendSmsData) => {
    if (!sendSmsData.message) {
      showToast.warning("Please enter a message");
      return;
    }
    const filterParams = buildFilterParams();

    try {
      const response = await axios.post(
        `${API_URL}subscription/send-sms-bulk`,
        {
          message: sendSmsData.message,
          checkingAll: checkingAll,
          selectedIds: selectedSubscriptions,
          filters: filterParams,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status == 200 || response.status == 201) {
        showToast.success(response.data.message || "Sms Send Successfully");
        setSelectedSubscriptions([]);
        fetchSubscriptions();
      }
    } catch (error) {
      console.log(error.response?.data || "Unknown error");
      showToast.error("An error occurred while sending sms");
    }
  };

  const handleSendPushSubmit = async (sendPushData) => {
    const filterParams = buildFilterParams();

    try {
      const response = await axios.post(
        `${API_URL}subscription/send-subscription-push`,
        {
          title: sendPushData.title,
          message: sendPushData.message,
          checkingAll: checkingAll,
          selectedIds: selectedSubscriptions,
          filters: filterParams,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status == 200 || response.status == 201) {
        showToast.success(
          response.data.message || "Push Notification Send Successfully"
        );
        setSelectedSubscriptions([]);
        fetchSubscriptions();
      }
    } catch (error) {
      console.log(error.response?.data || "Unknown error");
      showToast.error("An error occurred while sending push notification");
    }
  };

  return (
    <div className="panel-content mt-5">
      {loading && <LoadingSpinner />}
      <div className="panel-heading d-flex justify-content-between">
        <div className="col-md-4">
          <div
            style={{ float: "left", position: "relative", marginLeft: "5px" }}
          >
            <select
              value="" // No value needed for action dropdown
              onChange={(e) => {
                const selectedAction = e.target.value;
                if (selectedAction) {
                  handleActionChange(selectedAction);
                }
              }}
              style={{
                width: "110px",
                padding: "0.25rem 0.5rem",
                fontSize: "0.875rem",
                lineHeight: "1.5",
                borderRadius: "0.2rem",
                border: "1px solid #ced4da",
                appearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.5rem center",
                backgroundSize: "16px 12px",
              }}
            >
              <option value="">Action</option>
              <option value="delete">Delete Subscription</option>
              <option value="bulkEdit">Bulk Edit</option>
              <option value="addCredits">Add Credits Bulk</option>
              <option value="bulkExport">Bulk Export Data</option>
              <option value="sendSms">Send SMS</option>
              <option value="sendPush">Send Push Notification</option>
              <option value="sendEmail">Send Email</option>
            </select>
          </div>
        </div>
        <div>
          <span style={{ fontSize: "0.8rem" }}>
            {start} to {end} of {totalRecords}
          </span>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={handleRefresh}
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={toggleModalFreeService}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Free Service
          </button>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={toggleModalPaidServiceOrder}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Paid Service Order
          </button>
          <button
            className={`btn btn-default btn-sm ${page === 1 ? "d-none" : ""}`}
            title="Previous"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          &nbsp;
          <button
            className={`btn btn-default btn-sm ${
              page === totalPages ? "d-none" : ""
            }`}
            title="Next"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="large-table-container-3">
          <div
            // style={{
            //   overflowY: "auto",
            //   maxHeight: "400px",
            //   overflowX: "auto",
            // }}
            style={{
              overflowY: "auto",
              height: "calc(112vh - 250px)",
              overflowX: "auto",
              position: "relative",
            }}
          >
            <div className="d-flex justify-content-center">
              <button
                className={`btn ${
                  checkingAll ? "btn-primary" : "btn-default"
                } btn-xs ${
                  selectedSubscriptions.length === totalRecords
                    ? "d-block"
                    : "d-none"
                }`}
                onClick={toggleCheckingAll}
              >
                Select All the Records
              </button>
            </div>
            <table className="table table-condensed table-hover bg-white">
              <thead>
                <tr>
                  <th>
                    {/* Add a checkbox for selecting all subscribers */}
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        // Handle select all functionality
                        if (e.target.checked) {
                          // Select all subscribers on the current page
                          const allIds = filteredUsers.map((user) => user.id);
                          setSelectedSubscriptions(allIds);
                        } else {
                          // Deselect all subscribers
                          setSelectedSubscriptions([]);
                        }
                      }}
                      checked={
                        filteredUsers.length > 0 &&
                        selectedSubscriptions.length === filteredUsers.length
                      }
                    />
                  </th>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("created_on")}
                  >
                    Created Time{" "}
                    <FontAwesomeIcon icon={getSortIcon("created_on")} />
                  </th>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("fullname")}
                  >
                    Name <FontAwesomeIcon icon={getSortIcon("fullname")} />
                  </th>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("mobileno")}
                  >
                    Mobile No <FontAwesomeIcon icon={getSortIcon("mobileno")} />
                  </th>
                  <th>Lead Source</th>
                  <FieldPermissionWrapper field="subscriber_created_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("subscriber_created_date")}
                    >
                      Subscriber Created Date{" "}
                      <FontAwesomeIcon
                        icon={getSortIcon("subscriber_created_date")}
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("serviceid")}
                    >
                      Service{" "}
                      <FontAwesomeIcon icon={getSortIcon("serviceid")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_type">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("stype")}
                    >
                      Type <FontAwesomeIcon icon={getSortIcon("stype")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_status">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("status")}
                    >
                      Status <FontAwesomeIcon icon={getSortIcon("status")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service_type">
                    <th>Service Type</th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_amount">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("amount")}
                    >
                      Amount <FontAwesomeIcon icon={getSortIcon("amount")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_amount">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("actual_amount")}
                    >
                      Actual Amount{" "}
                      <FontAwesomeIcon icon={getSortIcon("actual_amount")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_activation_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("activation_date")}
                    >
                      Activation Date{" "}
                      <FontAwesomeIcon icon={getSortIcon("activation_date")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_expiry_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("expiry_date")}
                    >
                      Expiry Date{" "}
                      <FontAwesomeIcon icon={getSortIcon("expiry_date")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_onhold_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("onhold_date")}
                    >
                      On Hold Date{" "}
                      <FontAwesomeIcon icon={getSortIcon("onhold_date")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_reactivation_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("reactivation_date")}
                    >
                      Reactivation Date{" "}
                      <FontAwesomeIcon icon={getSortIcon("reactivation_date")} />
                    </th>
                  </FieldPermissionWrapper>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("trades_total")}
                  >
                    Total Credits{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_total")} />
                  </th>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("trades_used")}
                  >
                    Total Used{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_used")} />
                  </th>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("trades_available")}
                  >
                    Total Available{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_available")} />
                  </th>
                  <FieldPermissionWrapper field="subscription_sales_manager">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("sales_manager")}
                    >
                      Sales Manager{" "}
                      <FontAwesomeIcon icon={getSortIcon("sales_manager")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_assigned_user">
                    <th>Assigned User</th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_updated_by">
                    <th>Updated By </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmsource">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_source")}
                    >
                      UTM Source{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_source")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmmedium">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_medium")}
                    >
                      UTM Medium{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_medium")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcampaign">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_campaign")}
                    >
                      UTM Campaign{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_campaign")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcontent">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_content")}
                    >
                      UTM Content{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_content")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmterm">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_term")}
                    >
                      UTM Term{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_term")} />
                    </th>
                  </FieldPermissionWrapper>
                </tr>
                <tr>
                  <th></th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <div className="custom-datepicker-wrapper">
                        <DatePicker
                          selected={joinDateFilter[0]}
                          onChange={handleJoinDateChange}
                          startDate={joinDateFilter[0]}
                          endDate={joinDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={prevMonthStart}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => fetchSubscriptions()}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      style={{
                        width: "90px",
                        padding: "5px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchSubscriptions();
                      }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={mobileFilter}
                      onChange={(e) => setMobileFilter(e.target.value)}
                      style={{
                        width: "90px",
                        padding: "5px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchSubscriptions();
                      }}
                    />
                  </th>
                  <th>
                    <select
                      value={leadSourceFilter}
                      onChange={(e) => setLeadSourceFilter(e.target.value)}
                      style={{ width: "110px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.leadSourceList &&
                        dropdownOptions.leadSourceList.map((item, index) => (
                          <option key={index} value={item.pick_name}>
                            {item.pick_name}
                          </option>
                        ))}
                    </select>
                  </th>
                  <FieldPermissionWrapper field="subscriber_created_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={subscriberCreatedDateFilter[0]}
                          onChange={handleSubscriberDateChange}
                          startDate={subscriberCreatedDateFilter[0]}
                          endDate={subscriberCreatedDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={prevMonthStart}
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => fetchSubscriptions()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service">
                    <th>
                      <select
                        value={serviceFilter}
                        onChange={(e) => setServiceFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.serviceList &&
                          dropdownOptions.serviceList.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.service_name}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_type">
                    <th>
                      <select
                        value={subscriptionTypeFilter}
                        onChange={(e) =>
                          setSubscriptionTypeFilter(e.target.value)
                        }
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.subscriptionTypeList &&
                          dropdownOptions.subscriptionTypeList.map(
                            (item, index) => (
                              <option key={index} value={item.pick_name}>
                                {item.pick_name}
                              </option>
                            )
                          )}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_status">
                    <th>
                      <select
                        value={serviceStatusFilter}
                        onChange={(e) => setServiceStatusFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.serviceStatusList &&
                          dropdownOptions.serviceStatusList.map(
                            (item, index) => (
                              <option key={index} value={item.pick_name}>
                                {item.pick_name}
                              </option>
                            )
                          )}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service_type">
                    <th>
                      <select
                        value={serviceTypeFilter}
                        onChange={(e) => setServiceTypeFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.serviceTypeList &&
                          dropdownOptions.serviceTypeList.map((item, index) => (
                            <option key={index} value={item.pick_name}>
                              {item.pick_name}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_amount">
                    <th>
                      <input
                        type="text"
                        placeholder="e.g. 100-5000"
                        title="Enter single value or range (e.g. 100-500, >=100, <=500)"
                        value={amountFilter}
                        onChange={(e) => setAmountFilter(e.target.value)}
                        style={{
                          width: "90px",
                          padding: "5px",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") fetchSubscriptions();
                        }}
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_amount">
                    <th>
                      <input
                        type="text"
                        placeholder=""
                        value={actualAmountFilter}
                        onChange={(e) => setActualAmountFilter(e.target.value)}
                        style={{
                          width: "90px",
                          padding: "5px",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") fetchSubscriptions();
                        }}
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_activation_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={activtionDateFilter[0]}
                          onChange={handleActivationDateChange}
                          startDate={activtionDateFilter[0]}
                          endDate={activtionDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={prevMonthStart}
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => fetchSubscriptions()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_expiry_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={expiryDateFilter[0]}
                          onChange={handleExpiryDateChange}
                          startDate={expiryDateFilter[0]}
                          endDate={expiryDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={prevMonthStart}
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => fetchSubscriptions()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_onhold_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={onHoldDateFilter[0]}
                          onChange={handleOnHoldDateChange}
                          startDate={onHoldDateFilter[0]}
                          endDate={onHoldDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={prevMonthStart}
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => fetchSubscriptions()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_reactivation_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={reactivationDateFilter[0]}
                          onChange={handleReactivationDateChange}
                          startDate={reactivationDateFilter[0]}
                          endDate={reactivationDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={prevMonthStart}
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => fetchSubscriptions()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <th>
                    <input
                      type="text"
                      placeholder="e.g. 10-50"
                      title="Enter single value or range (e.g. 10-50, >=10, <=50)"
                      value={totalCreditsFilter}
                      onChange={(e) => setTotalCreditsFilter(e.target.value)}
                      style={{
                        width: "90px",
                        padding: "5px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchSubscriptions();
                      }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="e.g. 10-50"
                      title="Enter single value or range (e.g. 10-50, >=10, <=50)"
                      value={usedCreditsFilter}
                      onChange={(e) => setUsedCreditsFilter(e.target.value)}
                      style={{
                        width: "90px",
                        padding: "5px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchSubscriptions();
                      }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="e.g. 10-50"
                      title="Enter single value or range (e.g. 10-50, >=10, <=50)"
                      value={availableCreditsFilter}
                      onChange={(e) =>
                        setAvailableCreditsFilter(e.target.value)
                      }
                      style={{
                        width: "90px",
                        padding: "5px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchSubscriptions();
                      }}
                    />
                  </th>
                  <FieldPermissionWrapper field="subscription_sales_manager">
                    <th>
                      <select
                        value={salesManagerFilter}
                        onChange={(e) => setSalesManagerFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.userList &&
                          dropdownOptions.userList.map((item, index) => (
                            <option key={index} value={item.user_id}>
                              {item.user_fullname}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_assigned_user">
                    <th>
                      <select
                        value={assignedUserFilter}
                        onChange={(e) => setAssignedUserFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.userList &&
                          dropdownOptions.userList.map((item, index) => (
                            <option key={index} value={item.user_id}>
                              {item.user_fullname}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_updated_by">
                    <th>
                      <select
                        value={updatedByFilter}
                        onChange={(e) => setUpdatedByFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.fullUserList &&
                          dropdownOptions.fullUserList.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.user_fullname}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmsource">
                    <th>
                      <select
                        value={utmSourceFilter}
                        onChange={(e) => setUtmSourceFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmSourceList &&
                          dropdownOptions.utmSourceList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmmedium">
                    <th>
                      <select
                        value={utmMediumFilter}
                        onChange={(e) => setUtmMediumFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmMediumList &&
                          dropdownOptions.utmMediumList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcampaign">
                    <th>
                      <select
                        value={utmCampaignFilter}
                        onChange={(e) => setUtmCampaignFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">Select</option>
                        {dropdownOptions.utmCampaignList &&
                          dropdownOptions.utmCampaignList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcontent">
                    <th>
                      <select
                        value={utmContentFilter}
                        onChange={(e) => setUtmContentFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmContentList &&
                          dropdownOptions.utmContentList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmterm">
                    <th>
                      <select
                        value={utmTermFilter}
                        onChange={(e) => setUtmTermFilter(e.target.value)}
                        style={{ width: "110px", padding: "5px" }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmTermList &&
                          dropdownOptions.utmTermList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => handleRowClick(user.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedSubscriptions.includes(user.id)}
                        onChange={(e) =>
                          handleCheckboxChange(user?.id, e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      {user?.created_on
                        ? moment(user?.created_on).format("DD-MM-YYYY hh:mm A")
                        : ""}
                    </td>
                    <td>{user?.fullname || ""}</td>
                    <td>{user?.mobileno || ""}</td>
                    <td>{user?.lead_source || ""}</td>
                    <FieldPermissionWrapper field="subscriber_created_date">
                      <td>
                        {user?.subscriber_created_on
                          ? moment(user?.subscriber_created_on).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                          : ""}
                      </td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_service">
                      <td>{user?.service_name || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_type">
                      <td>{user?.stype || ""}</td>
                    </FieldPermissionWrapper>

                    <FieldPermissionWrapper field="subscription_status">
                      <td>{user?.status || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_service_type">
                      <td>{user?.servicetype || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_amount">
                      <td>{user?.amount || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_amount">
                      <td>{user?.actual_amount || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_activation_date">
                      <td>
                        {user?.activation_date
                          ? moment(user?.activation_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                          : ""}
                      </td>
                    </FieldPermissionWrapper>

                    <FieldPermissionWrapper field="subscription_expiry_date">
                      <td>
                        {user?.expiry_date
                          ? moment(user?.expiry_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                          : ""}
                      </td>
                    </FieldPermissionWrapper>

                    <FieldPermissionWrapper field="subscription_onhold_date">
                      <td>
                        {user?.onhold_date
                          ? moment(user?.onhold_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                          : ""}
                      </td>
                    </FieldPermissionWrapper>

                    <FieldPermissionWrapper field="subscription_reactivation_date">
                      <td>
                        {user?.reactivation_date
                          ? moment(user?.reactivation_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                          : ""}
                      </td>
                    </FieldPermissionWrapper>

                    <td>{user?.trades_total}</td>
                    <td>{user?.trades_used}</td>
                    <td>{user?.trades_available}</td>
                    <FieldPermissionWrapper field="subscription_sales_manager">
                      <td>{user?.sales_manager_name || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_assigned_user">
                      <td>{user?.assigned_user_name || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_updated_by">
                      <td>{user?.updated_by_name || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_utmsource">
                      <td>{user?.utm_source || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_utmmedium">
                      <td>{user?.utm_medium || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_utmcampaign">
                      <td>{user?.utm_campaign || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_utmcontent">
                      <td>{user?.utm_content || ""}</td>
                    </FieldPermissionWrapper>
                    <FieldPermissionWrapper field="subscription_utmterm">
                      <td>{user?.utm_term || ""}</td>
                    </FieldPermissionWrapper>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Backdrop */}
      {(isModalOpenPaidServiceOrder ||
        isModalOpenFreeService ||
        isModalOpenBulkEdit ||
        isModalOpenCreditsAdd ||
        isModalOpenSendSms ||
        isModalOpenSendPush) && (
        <div
          className="modal-backdrop fade show"
          style={{
            backgroundColor: "rgba(0, 0, 0, 1)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1040,
          }}
        ></div>
      )}

      {/* Modal structure */}
      {isModalOpenPaidServiceOrder && (
        <AddPaidServiceOrderPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
        />
      )}

      {isModalOpenFreeService && (
        <AddFreeServicePopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
        />
      )}

      {isModalOpenBulkEdit && (
        <BulkEditPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
          selectedSubscriptions={selectedSubscriptions}
          onSubmit={handleBulkEditSubmit}
          isLoading={isLoading}
        />
      )}

      {isModalOpenCreditsAdd && (
        <AddCreditsPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
        />
      )}

      {isModalOpenSendSms && (
        <SendSmsBulkPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          onSubmit={handleSendSmsSubmit}
        />
      )}

      {isModalOpenSendPush && (
        <SendPushNotificationPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          onSubmit={handleSendPushSubmit}
        />
      )}
      {isModalOpenSendEmail && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1040,
            }}
          ></div>

          {/* Modal */}
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{
              zIndex: 1050,
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              maxWidth: "900px",
            }}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              role="document"
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Send Bulk Email</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsModalOpenSendEmail(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <form
                    onSubmit={handleEmailSubmit}
                    encType="multipart/form-data"
                  >
                    <div className="mb-3">
                      <label>Select Email Template</label>
                      <select
                        className="form-control"
                        value={emailData.emailtemp}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                      >
                        <option value="">-- Select Template --</option>
                        {templateList.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>
                            {tpl.templatename}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label>Subject</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emailData.subject}
                        onChange={(e) =>
                          setEmailData({
                            ...emailData,
                            subject: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label>Message</label>
                      <ReactQuill
                        theme="snow"
                        value={emailData.message}
                        onChange={(value) =>
                          setEmailData({ ...emailData, message: value })
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label>Upload Attachment (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) =>
                          setEmailData({
                            ...emailData,
                            file: e.target.files[0],
                          })
                        }
                      />
                    </div>

                    <div className="d-flex justify-content-end">
                      <button type="submit" className="btn btn-primary me-2">
                        Send Email
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setIsModalOpenSendEmail(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Subscription;
